import { useQueryClient } from '@tanstack/react-query';
import { RealtimeTranscriber } from 'assemblyai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { useTranscriptBonusReason } from './use-transcript-bonus-reason';

export enum AudioState {
	Idle,
	Starting,
	Recording,
	Stopping,
	Complete,
}

const SAMPLE_RATE = 16_000;

const mergeBuffers = (lhs: Int16Array, rhs: Int16Array) => {
	const mergedBuffer = new Int16Array(lhs.length + rhs.length);
	mergedBuffer.set(lhs, 0);
	mergedBuffer.set(rhs, lhs.length);
	return mergedBuffer;
};

export const useVideoTranscription = (audioStream: MediaStream | null) => {
	const queryClient = useQueryClient();
	const http = useHttpClient();
	const toast = useToast();
	const { t } = useTranslation('video');
	const [audioState, setAudioState] = useState(AudioState.Idle);
	// TODO Rename useTranscriptBonusReason
	const { mutation: transcriptBonusReason } = useTranscriptBonusReason();

	// Ref state that doesn't need to trigger re-renders
	const stateRef = useRef({
		audioState,
		service: null as RealtimeTranscriber | null,
		rawText: '',
		audioContext: null as AudioContext | null,
		audioBufferQueue: new Int16Array(0),
		lastProcessedText: '',
		lastSpeechTagsResult: null as any,
	});

	// Update ref when state changes
	useEffect(() => {
		stateRef.current.audioState = audioState;
	}, [audioState]);

	const startTranscription = useCallback(async () => {
		if (!audioStream) {
			console.error('No audio stream available for transcription');
			return;
		}

		try {
			setAudioState(AudioState.Starting);
			stateRef.current.rawText = '';
			stateRef.current.lastProcessedText = '';
			stateRef.current.lastSpeechTagsResult = null;
			const tokenData = await queryClient.fetchQuery({
				queryKey: ['assemblyai-temp-token'],
				queryFn: async () => {
					const res = await http.post('/transcription/tokens');
					const data = res.data;
					const invalidateAfterMs = Math.max((data.expires_in - 30) * 1000, 1000);
					setTimeout(() => {
						queryClient.invalidateQueries(['assemblyai-temp-token']);
					}, invalidateAfterMs);

					return data;
				},
				// Longer staleTime since we're manually managing invalidation
				staleTime: 5 * 60 * 1000, // 5 minutes fallback
			});

			const token = tokenData.token;

			const service = new RealtimeTranscriber({ token });
			const textChunks: { [k: number]: string } = {};

			service.on('transcript', (message: { audio_start: number; text: string }) => {
				if (message.text) {
					textChunks[message.audio_start] = message.text;
					const text = Object.entries(textChunks)
						.sort(([a], [b]) => Number(a) - Number(b))
						.map(([, text]) => text)
						.join(' ');

					stateRef.current.rawText = text;
					console.log('raw text', text);
				}
			});

			await service.connect();

			// Create audio context and connect to the existing stream
			const audioContext = new AudioContext({
				sampleRate: SAMPLE_RATE,
				latencyHint: 'balanced',
			});
			const source = audioContext.createMediaStreamSource(audioStream);

			await audioContext.audioWorklet.addModule('/worklets/float32-to-int16-audio.js');
			const audioWorkletNode = new AudioWorkletNode(audioContext, 'float32-to-int16-audio');

			source.connect(audioWorkletNode).connect(audioContext.destination);

			audioWorkletNode.port.onmessage = (event) => {
				if (!event.data.audio_data) return;

				const currentBuffer = new Int16Array(event.data.audio_data);
				stateRef.current.audioBufferQueue = mergeBuffers(stateRef.current.audioBufferQueue, currentBuffer);

				// Determine the number of milliseconds of audio data we have in the buffer
				const bufferDuration = (stateRef.current.audioBufferQueue.length / audioContext.sampleRate) * 1000;

				// Only send the buffer if it's at least 100ms long
				if (bufferDuration >= 100) {
					const totalSamples = Math.floor(audioContext.sampleRate * 0.1);
					const finalBuffer = stateRef.current.audioBufferQueue.subarray(0, totalSamples).buffer;
					stateRef.current.audioBufferQueue = stateRef.current.audioBufferQueue.subarray(totalSamples);
					// Send the buffer as an ArrayBuffer
					service.sendAudio(finalBuffer);
				}
			};

			stateRef.current.audioContext = audioContext;
			stateRef.current.service = service;
			setAudioState(AudioState.Recording);
			console.log('Video transcription started successfully');
		} catch (err) {
			console.error('Error starting video transcription:', err);
			setAudioState(AudioState.Idle);
		}
	}, [audioStream, http, queryClient, t, toast]);

	const stopTranscription = useCallback(async () => {
		setAudioState(AudioState.Stopping);
		if (stateRef.current.audioContext) {
			await stateRef.current.audioContext.close();
			stateRef.current.audioContext = null;
		}

		if (stateRef.current.service) {
			await stateRef.current.service.close();
			stateRef.current.service = null;
		}

		stateRef.current.audioBufferQueue = new Int16Array(0);
		setAudioState(AudioState.Complete);
		console.log('Video transcription stopped');
	}, []);

	const getSpeechTags = useCallback(async () => {
		const transcript_text = stateRef.current.rawText.trim();

		// Return null if text is empty
		if (!transcript_text) {
			return null;
		}

		// If text hasn't changed, return cached result
		if (transcript_text === stateRef.current.lastProcessedText) {
			return stateRef.current.lastSpeechTagsResult;
		}

		try {
			const result = await transcriptBonusReason.mutateAsync({ transcript_text: transcript_text, preview_mode: true });

			// Update cache only after successful call
			if (result) {
				stateRef.current.lastProcessedText = transcript_text;
				stateRef.current.lastSpeechTagsResult = result;
			}

			return result;
		} catch (error) {
			console.error('Error getting speech tags:', error);
			return stateRef.current.lastSpeechTagsResult;
		}
	}, [transcriptBonusReason]);

	const getSuggestedBonusReason = useCallback(async () => {
		const currentText = stateRef.current.rawText.trim();
		console.log('currentText when getting suggested bonus reason', currentText);
		if (currentText) {
			try {
				const result = await transcriptBonusReason.mutateAsync({ transcript_text: currentText });
				return result?.bonus_reason || '';
			} catch (error: any) {
				console.error('Error calling transcript bonus reason API:', error);
				return null;
			}
		}

		return null;
	}, [transcriptBonusReason]);

	const clearTranscriptText = useCallback(() => {
		stateRef.current.rawText = '';
		stateRef.current.lastProcessedText = '';
		stateRef.current.lastSpeechTagsResult = null;
	}, []);

	return {
		isTranscribing: audioState === AudioState.Recording,
		isLoadingSuggestedReason: transcriptBonusReason.isLoading,
		transcriptText: stateRef.current.rawText,
		startTranscription,
		stopTranscription,
		getSuggestedBonusReason,
		getSpeechTags,
		clearTranscriptText,
	};
};
